import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Zoom
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

function StatsCard({
  title,
  value,
  subtitle,
  icon: IconComponent,
  color = 'primary.main',
  trend,
  trendLabel = 'from last month',
  loading = false,
  onClick,
  onMenuClick,
  gradient = false,
  sx = {}
}) {
  const getTrendColor = () => {
    if (trend > 0) return 'success.main';
    if (trend < 0) return 'error.main';
    return 'text.secondary';
  };

  const getTrendIcon = () => {
    if (trend > 0) return TrendingUpIcon;
    if (trend < 0) return TrendingDownIcon;
    return null;
  };

  const TrendIcon = getTrendIcon();

  return (
    <Zoom in timeout={300}>
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease-in-out',
          background: gradient 
            ? `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`
            : 'background.paper',
          '&:hover': onClick ? {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          } : {},
          ...sx
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flexGrow: 1 }}>
              <Typography 
                variant="subtitle2" 
                color="text.secondary" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  mb: 1
                }}
              >
                {title}
              </Typography>
              
              <Fade in timeout={400}>
                <Typography 
                  variant="h4" 
                  component="div" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 1,
                    lineHeight: 1.2
                  }}
                >
                  {loading ? '...' : value}
                </Typography>
              </Fade>
              
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {subtitle}
                </Typography>
              )}
              
              {trend !== undefined && (
                <Fade in timeout={500}>
                  <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
                    {TrendIcon && (
                      <TrendIcon 
                        sx={{ 
                          color: getTrendColor(), 
                          mr: 0.5, 
                          fontSize: 18 
                        }} 
                      />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: getTrendColor(),
                        fontWeight: 600,
                        mr: 1
                      }}
                    >
                      {trend > 0 ? '+' : ''}{trend}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {trendLabel}
                    </Typography>
                  </Box>
                </Fade>
              )}
            </Box>
            
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
              {IconComponent && (
                <Avatar
                  sx={{
                    bgcolor: color,
                    width: 56,
                    height: 56,
                    boxShadow: 2,
                    '& svg': {
                      fontSize: 28
                    }
                  }}
                >
                  <IconComponent />
                </Avatar>
              )}
              
              {onMenuClick && (
                <Tooltip title="More options">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuClick(e);
                    }}
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
        
        {/* Decorative gradient bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${color}, ${color}80)`,
            borderRadius: '12px 12px 0 0'
          }}
        />
      </Card>
    </Zoom>
  );
}

export default StatsCard;